import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { LockKeyhole, Search } from "lucide-react";
import { get } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { formatDate } from "@/lib/constants";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";


export default function LockChanges() {
  const navigate = useNavigate();
  const [f, setF] = useState({ q: "" });
  const { data, loading, error, reload } = useAsync(() => get("/reports/lock-changes"), []);
  const rows = useMemo(() => (data || []).filter((r) =>
    [r.postcode, r.full_address, r.lock_type, r.key_safe_code, r.key_return_phone, r.locksmith, r.officer].join(" ").toLowerCase().includes(f.q.toLowerCase().trim())), [data, f.q]);

  return (
    <div className="space-y-6" data-testid="lock-changes-page">
      <PageHeader eyebrow="Reports" title="Lock Changes" description="Every address where the lock was changed." />
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search postcode, address, lock type, code, locksmith" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="pl-9" aria-label="Search lock changes" data-testid="lock-changes-search-input" />
        </div>
      </div>
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows />}
      {data && !rows.length && <EmptyState icon={LockKeyhole} title="No lock changes found" description="Lock changes recorded on addresses will appear here." testId="lock-changes-empty" />}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500" data-testid="lock-changes-count">{rows.length} lock change{rows.length === 1 ? "" : "s"}</p>
          <Table data-testid="lock-changes-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Run date</TableHead><TableHead>Address</TableHead><TableHead>Lock type</TableHead><TableHead>Key Safe Code</TableHead>
              <TableHead>Recorded</TableHead><TableHead>Locksmith</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/manager/runs/${r.run_id}`)} data-testid={`lock-change-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap font-medium">{formatDate(r.run_date)}</TableCell>
                  <TableCell><div className="text-sm font-medium text-slate-800">{r.full_address}</div><div className="font-data text-sm font-bold text-blue-800">{r.postcode}</div></TableCell>
                  <TableCell className="text-sm">{r.lock_type || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {r.no_key_safe ? <div className="font-semibold text-amber-800">No key safe</div> : <div className="font-data font-semibold">{r.key_safe_code || "—"}</div>}
                    <div className="text-xs text-slate-600">Key return phone: <span className="font-data">{r.phone_left ? r.key_return_phone || "Yes" : "Not left"}</span></div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-slate-600">{r.lock_changed_at ? format(parseISO(r.lock_changed_at), "d MMM yyyy, HH:mm") : "—"}</TableCell>
                  <TableCell className="text-sm">{r.locksmith || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
